import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { bookmarkApiClient } from '@src/api/company/bookmark/bookmarkApiClient';
import { BookmarkItem } from '@src/api/types/bookmark';
import { Pagination } from '@src/api/types/pagination';
import PaginationComponent from '@src/components/Pagination';
import Th from '@src/ui/elements/table/Th';
import Title from '@src/ui/elements/typography/Title';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const BookmarkListForm: React.FC = () => {
  const [bookmarks, setBookmarks] = useState<BookmarkItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBookmarks = async () => {
      setIsLoading(true);
      const query = new URLSearchParams(location.search);
      const page = parseInt(query.get('page') || '1', 10);
      const res = await bookmarkApiClient.index({ page });
      handleApiResponse(res, (data) => {
        setBookmarks(data.data);
        setPagination(data.pagination);
      });
      setIsLoading(false);
    };
    fetchBookmarks();
  }, [location.search]);

  const handlePageChange = (page: number) => {
    const query = new URLSearchParams(window.location.search);
    query.set('page', page.toString());
    navigate({ search: query.toString() });
  };

  return (
    <main className='px-6 pb-12'>
      <div>
        <div className='mt-12'>
          <Title>ブックマーク一覧</Title>
        </div>

        {/* PC版テーブル */}
        <div
          className='mt-4 overflow-auto md:hidden'
          data-testid='bookmark-table-pc'
        >
          <table className='size-full min-w-[700px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[140px]' />
              <col />
              <col className='w-[180px]' />
              <col className='w-[100px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th>公開日</Th>
                <Th>記事タイトル</Th>
                <Th>カテゴリ</Th>
                <Th>いいね数</Th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && bookmarks.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <p
                      className='px-3 py-6 text-sm'
                      data-testid='empty-message-pc'
                    >
                      データがありません。
                    </p>
                  </td>
                </tr>
              ) : (
                bookmarks.map((bookmark) => (
                  <tr
                    key={bookmark.article_id}
                    className='[&:nth-child(2n)]:bg-gray-10'
                  >
                    <td>
                      <div className='truncate px-3 py-6'>{bookmark.published_at}</div>
                    </td>
                    <td>
                      <div className='truncate px-3 py-6'>
                        <Link
                          to={`/articles/${bookmark.article_id}`}
                          className='text-primary hover:underline'
                        >
                          {bookmark.title}
                        </Link>
                      </div>
                    </td>
                    <td>
                      <div className='truncate px-3 py-6'>{bookmark.category?.name}</div>
                    </td>
                    <td>
                      <div className='px-3 py-6'>{bookmark.favorites_count}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* SP版テーブル */}
        <div
          className='mt-4 hidden md:block'
          data-testid='bookmark-table-sp'
        >
          <table className='size-full table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th>記事タイトル</Th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && bookmarks.length === 0 ? (
                <tr>
                  <td>
                    <p
                      className='px-3 py-4 text-sm'
                      data-testid='empty-message-sp'
                    >
                      データがありません。
                    </p>
                  </td>
                </tr>
              ) : (
                bookmarks.map((bookmark) => (
                  <tr
                    key={bookmark.article_id}
                    className='[&:nth-child(2n)]:bg-gray-10'
                  >
                    <td>
                      <div className='truncate px-3 py-4'>
                        <Link
                          to={`/articles/${bookmark.article_id}`}
                          className='text-primary hover:underline'
                        >
                          {bookmark.title}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total !== 0 && (
          <div
            className='mt-6'
            data-testid='bookmark-pagination'
          >
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </main>
  );
};

export default BookmarkListForm;
